import { useState } from 'react';
import { Download, Heart, MessageSquare, Repeat2 } from 'lucide-react';
import { Dialog } from './ui/dialog';
import { cn } from '@/lib/cn';

interface PostOwner {
  uid: string | null;
  username: string | null;
  name: string | null;
  profilePhoto: string | null;
}

export interface LightboxPost {
  _id: string;
  caption: string | null;
  post_type: string;
  status: string;
  review_status: string;
  is_hidden?: boolean;
  is_reported?: boolean;
  likes_count: number;
  comments_count: number;
  reposts_count?: number;
  createdAt: string;
  thumbnail: string | null;
  owner: PostOwner;
  // Optional — when present we'll use the first link as the full media URL.
  // The backend already gives us `thumbnail` which is sufficient for photos.
  // For clips we fall back to thumbnail if a direct video URL isn't available.
  mediaUrl?: string | null;
}

interface PostLightboxProps {
  post: LightboxPost | null;
  onClose: () => void;
  /** Optional moderation action buttons to render in the footer. */
  actions?: React.ReactNode;
  /** Optional analysis block (e.g., Sightengine breakdown) above the actions. */
  analysis?: React.ReactNode;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusPill({
  label,
  tone,
}: {
  label: string;
  tone: 'cyan' | 'warning' | 'danger' | 'muted';
}): JSX.Element {
  const cls = {
    cyan: 'bg-state-success/10 text-brand-cyan',
    warning: 'bg-state-warning/15 text-state-warning',
    danger: 'bg-state-danger/15 text-state-danger',
    muted: 'bg-surface-divider/30 text-fg-subtle',
  }[tone];
  return (
    <span
      className={cn(
        'inline-block rounded-md px-2 py-0.5 text-xs font-medium capitalize',
        cls,
      )}
    >
      {label}
    </span>
  );
}

export function PostLightbox({
  post,
  onClose,
  actions,
  analysis,
}: PostLightboxProps): JSX.Element {
  return (
    <Dialog
      open={post !== null}
      onClose={onClose}
      size="xl"
      label="Post preview"
    >
      {post && (
        <div className="grid gap-6 md:grid-cols-[1.4fr_1fr]">
          {/* Media */}
          <ClipOrImage post={post} />

          {/* Meta */}
          <div className="flex flex-col gap-4 pr-4">
            <div className="flex items-center gap-3">
              {post.owner.profilePhoto ? (
                <img
                  src={post.owner.profilePhoto}
                  alt=""
                  className="h-10 w-10 rounded-full object-cover border border-surface-divider"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-surface-bg border border-surface-divider" />
              )}
              <div className="min-w-0">
                <div className="font-bold truncate">
                  {post.owner.username ?? post.owner.name ?? '(unknown)'}
                </div>
                {post.owner.username && post.owner.name && (
                  <div className="text-xs text-fg-subtle truncate">
                    {post.owner.name}
                  </div>
                )}
              </div>
            </div>

            <div className="text-sm text-fg-muted whitespace-pre-wrap break-words">
              {post.caption?.trim() ? (
                post.caption
              ) : (
                <span className="text-fg-subtle italic">no caption</span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3 pt-2">
              <Stat icon={<Heart size={14} />} label="Likes" value={post.likes_count} />
              <Stat
                icon={<MessageSquare size={14} />}
                label="Comments"
                value={post.comments_count}
              />
              <Stat
                icon={<Repeat2 size={14} />}
                label="Reposts"
                value={post.reposts_count ?? 0}
              />
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <StatusPill label={post.post_type} tone="muted" />
              <StatusPill label={post.status} tone="muted" />
              <StatusPill
                label={`review: ${post.review_status}`}
                tone={
                  post.review_status === 'rejected'
                    ? 'danger'
                    : post.review_status === 'pending'
                      ? 'warning'
                      : post.review_status === 'approved'
                        ? 'cyan'
                        : 'muted'
                }
              />
              {post.is_hidden && (
                <StatusPill label="hidden" tone="danger" />
              )}
              {post.is_reported && (
                <StatusPill label="reported" tone="warning" />
              )}
            </div>

            <div className="text-xs text-fg-subtle pt-2 border-t border-surface-divider">
              Posted {formatDate(post.createdAt)}
            </div>

            {analysis && (
              <div className="pt-3 border-t border-surface-divider">
                {analysis}
              </div>
            )}

            {actions && (
              <div className="pt-2 border-t border-surface-divider">
                <div className="text-xs font-bold uppercase tracking-wide text-fg-muted mb-2">
                  Actions
                </div>
                {actions}
              </div>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

function ClipOrImage({ post }: { post: LightboxPost }): JSX.Element {
  const [videoError, setVideoError] = useState<string | null>(null);

  if (post.post_type === 'clip') {
    if (!post.mediaUrl) {
      return (
        <div className="flex items-center justify-center bg-black rounded-xl overflow-hidden min-h-[300px]">
          <div className="p-12 text-center text-fg-subtle">
            Video URL not available — clip may be incomplete (status:{' '}
            {post.status}).
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center bg-black rounded-xl overflow-hidden min-h-[300px]">
        {!videoError ? (
          <video
            key={post.mediaUrl}
            src={post.mediaUrl}
            poster={post.thumbnail ?? undefined}
            controls
            playsInline
            preload="auto"
            className="max-h-[70vh] w-full"
            onError={(e) => {
              const el = e.currentTarget;
              const err = el.error;
              setVideoError(
                err
                  ? `${err.message || 'Unknown error'} (code ${err.code})`
                  : 'Browser cannot load this video',
              );
            }}
          />
        ) : (
          <div className="p-8 text-center">
            <div className="text-sm text-state-warning mb-2">
              {videoError}
            </div>
            <div className="text-xs text-fg-subtle mb-4">
              The video format may not be supported by this browser.
            </div>
          </div>
        )}
        <a
          href={post.mediaUrl}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 my-2 rounded-md text-xs font-medium text-fg-muted bg-surface-raised border border-surface-divider hover:text-fg transition-colors"
        >
          <Download size={12} />
          Download video
        </a>
      </div>
    );
  }

  if (post.thumbnail) {
    return (
      <div className="flex items-center justify-center bg-black rounded-xl overflow-hidden min-h-[300px]">
        <img
          src={post.thumbnail}
          alt={post.caption ?? 'Post preview'}
          className="max-h-[70vh] w-auto object-contain"
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center bg-black rounded-xl overflow-hidden min-h-[300px]">
      <div className="p-12 text-center text-fg-subtle">
        No media available for this post.
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-xs uppercase tracking-wide text-fg-muted flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-lg font-extrabold tabular-nums">
        {value.toLocaleString()}
      </div>
    </div>
  );
}
